/**
 * Camera tuning constants — the web equivalent of the data-driven camera
 * speeds Unity reads from `AppSettings`.
 *
 * Keep these here (or read from a future `models/settings/AppSettingsModel`)
 * so feature code never hardcodes camera sensitivity. Mirrors the rule from
 * `Campfire_Web_Runtime`'s camera section:
 *
 *   "Camera speed/sensitivity values must come from an AppSettings equivalent,
 *    not be hardcoded in the camera file."
 */

export interface CameraSettings {
  readonly defaultPosition: readonly [number, number, number];
  readonly defaultTarget: readonly [number, number, number];
  readonly defaultRadius: number;
  readonly orbitSensitivity: number;
  readonly panSensitivity: number;
  /**
   * How far one wheel notch moves the camera, as a share of its distance to the
   * pivot: desktop's `DesktopSceneInput.m_zoomSens`, where one
   * `Input.mouseScrollDelta` notch is 1.
   */
  readonly wheelZoomPerNotch: number;
  /**
   * Closer than this to the pivot, a wheel notch moves a FIXED
   * `wheelZoomPerNotch × wheelZoomNearDistance` instead of a share of the
   * distance, so zooming in never slows to a stop: Battlehub's
   * `Mathf.Max(ZoomSensitivity * 10, |m_orbitDistance|)`, with the desktop
   * prefab's `ZoomSensitivity` of 1.
   */
  readonly wheelZoomNearDistance: number;
  readonly lookMoveSpeed: number;
  /**
   * Exponent applied to the two-finger pinch distance ratio. 1 = the framing
   * tracks the fingers exactly (a 2× spread halves the orbit radius); higher
   * values zoom faster than the fingers move.
   */
  readonly pinchZoomSensitivity: number;
  readonly minRadius: number;
  readonly maxRadius: number;
  readonly minBeta: number;
  readonly maxBeta: number;
  readonly inertia: number;
  readonly fovDegrees: number;
  readonly nearClip: number;
  readonly farClip: number;
  /** Length of the camera glide used by `OrbitCameraController.focus`. */
  readonly focusTransitionSeconds: number;
}

export const DefaultCameraSettings: CameraSettings = Object.freeze({
  // Ported from Campfire's desktop scene camera in
  // `CampfireScenes/Assets/Campfire/Content/Infrastructure/RTEditor/SceneView Variant.prefab`
  // (the Campfire override of Battlehub's base SceneView) — camera transform
  // pos (0.833, 0.548, 0.796), euler (10, -135, 0). The pivot is forced to the
  // PlacementModel anchor by `SceneSetup.cs`. Unity world → Three world uses the
  // canonical M = diag(-1, 1, 1) (see `@models/utils/unityBasis`), so
  // (0.833, 0.548, 0.796) → (-0.833, 0.548, 0.796). This is ~1.17 units from
  // the anchor at ~10° elevation, matching the Unity default view.
  //
  // The Z sign changed with task 0004: the port's world map used to be
  // diag(-1, 1, -1), and this constant was hand-derived under it. Because the
  // orbit target sits on the Y axis, a wrong Z here yields a view 180° around
  // from Unity's that still looks entirely plausible — so it will not be caught
  // by eye. Re-derive it from the prefab value above, never from a screenshot.
  //
  // NOTE: earlier this was [10, 10, -10] (Battlehub's *base* prefab value),
  // which produced a too-steep ~34° top-down angle that did not match Unity.
  defaultPosition: [-0.833, 0.548, 0.796] as const,
  defaultTarget: [0, 0.343, 0] as const,
  // Derived distance of the default pose (|defaultPosition - defaultTarget|).
  // `applyDefaultPose` places the camera at the literal `defaultPosition`, so
  // this value is informational; it is not used to rescale the default pose.
  defaultRadius: 1.17,
  orbitSensitivity: 1.0,
  panSensitivity: 1.0,
  // Desktop's wheel zoom (2026-10-05, user-directed parity): 5% of the distance
  // per notch, never less than 0.5 m. This replaced a web-only 10% per notch
  // with no floor, which shrank to nothing near the pivot and stalled there.
  wheelZoomPerNotch: 0.05,
  wheelZoomNearDistance: 10,
  lookMoveSpeed: 1.5,
  pinchZoomSensitivity: 1.0,
  minRadius: 0.1,
  maxRadius: 500,
  minBeta: 0.05,
  maxBeta: Math.PI - 0.05,
  inertia: 0.85,
  fovDegrees: 60,
  nearClip: 0.05,
  farClip: 5000,
  // Battlehub's `RuntimeSceneComponent.Focus` (the desktop client's focus path,
  // reached from Spark's `focus_on_part`) animates over a hardcoded
  // `const float duration = 1.0f` with EaseOutCubic. Same value here so the two
  // clients feel identical.
  focusTransitionSeconds: 1.0,
});
